// Public archive search at `/reports`. Structure from Figma Make's
// ArchiveSearchPage — text search + year filter + paginated result list.
//
// TODO: replace placeholder data with a loader + pager once the backend
// search endpoint exists.

import { Button, Card, CardContent, CardHeader, CardTitle, H1, Input, P } from '@podium/platform/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

type Report = {
  id: string
  companyName: string
  eik: string
  year: number
  publicationDate: string
  city: string
}

const PLACEHOLDER_REPORTS: Report[] = []

const AVAILABLE_YEARS = [2026, 2025, 2024, 2023, 2022] as const

export const Route = createFileRoute('/reports')({
  component: ReportsPage,
})

function ReportsPage() {
  const [query, setQuery] = useState('')
  const [year, setYear] = useState<number | null>(null)

  const filtered = PLACEHOLDER_REPORTS.filter((r) => {
    const matchQ =
      !query || r.companyName.toLowerCase().includes(query.toLowerCase()) || r.eik.includes(query)
    const matchY = !year || r.year === year
    return matchQ && matchY
  })

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="mb-6 space-y-2">
        <H1>Публикувани годишни финансови отчети</H1>
        <P className="text-muted-foreground">
          Архивът съдържа отчетите, публикувани в съответствие с{' '}
          <a href="/law#csch-38-7">чл. 38, ал. 7 от ЗСч</a>. Достъпът е свободен и неплатен за
          минимум 3 години от датата на публикуване.
        </P>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <form
            className="flex flex-col gap-3 sm:flex-row"
            role="search"
            onSubmit={(e) => e.preventDefault()}
          >
            <Input
              name="q"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Наименование или ЕИК / БУЛСТАТ"
              className="flex-1"
            />
            <Button type="submit">Търси</Button>
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Година:</span>
            <Button
              variant={year === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setYear(null)}
            >
              Всички
            </Button>
            {AVAILABLE_YEARS.map((y) => (
              <Button
                key={y}
                variant={year === y ? 'default' : 'outline'}
                size="sm"
                onClick={() => setYear(y)}
              >
                {y}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <P className="mb-4 text-sm text-muted-foreground">Намерени {filtered.length} записа.</P>

      {filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((r) => (
            <Card key={r.id} className="transition-shadow hover:shadow-md">
              <CardContent className="pt-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <a
                      href={`/reports/${r.id}`}
                      className="font-semibold no-underline hover:underline"
                    >
                      {r.companyName}
                    </a>
                    <P className="mt-1 text-sm text-muted-foreground">
                      ЕИК: {r.eik} · {r.city} · отчет за {r.year} година · публикуван{' '}
                      {r.publicationDate}
                    </P>
                  </div>
                  <Button asChild variant="outline" size="sm">
                    <a href={`/reports/${r.id}`}>Преглед</a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Няма намерени резултати</CardTitle>
          </CardHeader>
          <CardContent>
            <P className="text-sm text-muted-foreground">
              Опитайте с друго наименование или изтрийте филтъра по година.
            </P>
          </CardContent>
        </Card>
      )}

      {/* TODO: add pager (prev / next + page numbers) once loader is wired. */}
    </div>
  )
}
